/**
 * Fetch seguro con validaciones adicionales
 */

import { apiRateLimiter, deleteRateLimiter, createRateLimiter } from './rate-limiter';
import { sanitizeObject } from './sanitize';

interface SecureFetchOptions extends RequestInit {
  skipRateLimit?: boolean;
  skipSanitize?: boolean;
  rateLimitKey?: string;
}

/**
 * Wrapper seguro de fetch con rate limiting y sanitización
 */
export async function secureFetch(
  url: string,
  options: SecureFetchOptions = {}
): Promise<Response> {
  const { skipRateLimit, skipSanitize, rateLimitKey, ...fetchOptions } = options;
  
  // Determinar el rate limiter según el método
  const method = (fetchOptions.method || 'GET').toUpperCase();
  const key = rateLimitKey || `${method}:${new URL(url, window.location.origin).pathname}`;
  
  // Aplicar rate limiting
  if (!skipRateLimit) {
    let limiter = apiRateLimiter;
    if (method === 'DELETE') {
      limiter = deleteRateLimiter;
    } else if (method === 'POST') {
      limiter = createRateLimiter;
    }
    
    if (!limiter.check(key)) {
      throw new Error('Demasiadas solicitudes. Por favor, esperá un momento.');
    }
  }
  
  // Sanitizar body si es JSON
  if (!skipSanitize && fetchOptions.body && typeof fetchOptions.body === 'string') {
    try {
      const parsed = JSON.parse(fetchOptions.body);
      const sanitized = sanitizeObject(parsed);
      fetchOptions.body = JSON.stringify(sanitized);
    } catch {
      // No es JSON, dejarlo como está
    }
  }
  
  // Agregar headers de seguridad
  const headers = new Headers(fetchOptions.headers);
  if (!headers.has('Content-Type') && fetchOptions.body) {
    headers.set('Content-Type', 'application/json');
  }
  
  // Realizar el fetch
  const response = await fetch(url, {
    ...fetchOptions,
    headers,
    credentials: 'same-origin',
  });
  
  return response;
}

/**
 * Verifica que una URL sea segura (mismo origen o dominios permitidos)
 */
export function isUrlSafe(url: string, allowedDomains: string[] = []): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    
    // Mismo origen siempre es seguro
    if (parsed.origin === window.location.origin) {
      return true;
    }
    
    // Verificar dominios permitidos
    if (allowedDomains.length > 0) {
      return allowedDomains.some(domain => 
        parsed.hostname === domain || parsed.hostname.endsWith(`.${domain}`)
      );
    }
    
    return false;
  } catch {
    return false;
  }
}

/**
 * Verifica que un redirect sea seguro
 */
export function isSafeRedirect(url: string): boolean {
  try {
    const parsed = new URL(url, window.location.origin);
    
    // Solo permitir mismo origen o rutas relativas
    if (parsed.origin !== window.location.origin) {
      return false;
    }
    
    // No permitir data: o javascript:
    if (url.startsWith('data:') || url.startsWith('javascript:')) {
      return false;
    }
    
    return true;
  } catch {
    return false;
  }
}
